/*
  Renderer for the full validation response. Splits the result into
  Valid / Invalid / Cancellations sections with row-selection wiring and
  surfaces file-level issues (mandatory fields missing, unknown status) in
  a single collapsible card.
*/
import React from 'react';
import {
    Alert,
    Button,
    Card,
    Col,
    Collapse,
    Empty,
    Row,
    Space,
    Table,
    Tag,
    Typography,
} from 'antd';
import { CheckCircleTwoTone, CloseCircleTwoTone } from '@ant-design/icons';
import { isValid, isValidArray, isValidArrayWithValues } from '../../utils/utilities';
import { SEAT_STATUS_LABELS } from './constants';
import { buildValidationBreakdown } from './payload-builders';

const { Text, Paragraph } = Typography;
const { Panel } = Collapse;

const renderSeatVerdict = (seat) => {
    const meta = SEAT_STATUS_LABELS[seat.status] || { color: 'default', text: seat.status || 'unknown' };
    return (
        <Tag color={meta.color}>
            {seat.seatNumber || '?'}
            {' — '}
            {meta.text}
        </Tag>
    );
};

const renderSeatTags = (seats) => (
    <Space size={[4, 4]} wrap>
        {seats.map((seat, idx) => (
            <span key={`${seat.seatNumber}-${idx}`}>
                {renderSeatVerdict(seat)}
            </span>
        ))}
    </Space>
);

const renderTravellerList = (names) => {
    if (!isValidArrayWithValues(names)) {
        return <Text type="secondary">—</Text>;
    }
    return (
        <div>
            {names.map((name, idx) => (
                <div key={`tr-${idx}`}>{name}</div>
            ))}
        </div>
    );
};

const validTicketColumns = [
    { title: 'Journey Date', dataIndex: 'journeyDate', key: 'journeyDate', width: 110 },
    { title: 'Service', dataIndex: 'serviceNumber', key: 'serviceNumber', width: 170 },
    { title: 'Route', dataIndex: 'route', key: 'route' },
    {
        title: 'Boarding → Drop',
        key: 'boardingDrop',
        render: (_v, r) => (
            <span>
                <Text>{r.boardingPoint || '?'}</Text>
                {' → '}
                <Text>{r.dropOffPoint || '?'}</Text>
            </span>
        ),
    },
    { title: 'PNR (file)', dataIndex: 'pnr', key: 'pnr', width: 110 },
    {
        title: 'Seats',
        key: 'seats',
        render: (_v, r) => renderSeatTags(r.seats),
    },
    {
        title: 'Travellers',
        key: 'travellers',
        render: (_v, r) => renderTravellerList(r.travellerNames),
    },
    {
        title: 'Fare',
        dataIndex: 'totalFare',
        key: 'totalFare',
        width: 90,
        render: (value) => (typeof value === 'number' ? value.toFixed(2) : value),
    },
];

// Invalid-ticket columns are built dynamically so the "Pick another seat"
// action can be wired to the page-level reassign modal opener.
const buildInvalidTicketColumns = (onOpenReassign) => [
    { title: 'Journey Date', dataIndex: 'journeyDate', key: 'journeyDate', width: 110 },
    { title: 'Service', dataIndex: 'serviceNumber', key: 'serviceNumber', width: 170 },
    { title: 'Route', dataIndex: 'route', key: 'route' },
    {
        title: 'Boarding → Drop',
        key: 'boardingDrop',
        render: (_v, r) => (
            <span>
                <Text>{r.boardingPoint || '?'}</Text>
                {' → '}
                <Text>{r.dropOffPoint || '?'}</Text>
            </span>
        ),
    },
    { title: 'PNR (file)', dataIndex: 'pnr', key: 'pnr', width: 110 },
    {
        title: 'Seats',
        key: 'seats',
        render: (_v, r) => renderSeatTags(r.seats),
    },
    {
        title: 'Travellers',
        key: 'travellers',
        render: (_v, r) => renderTravellerList(r.travellerNames),
    },
    {
        title: 'Reason(s) it cannot be imported',
        key: 'issues',
        render: (_v, r) => (
            isValidArrayWithValues(r.issues)
                ? (
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                        {r.issues.map((issue, idx) => (
                            <li key={`issue-${idx}`}>{issue}</li>
                        ))}
                    </ul>
                )
                : <Text type="secondary">{r.tripIssue || 'No specific reason returned'}</Text>
        ),
    },
    {
        title: 'Action',
        key: 'action',
        width: 160,
        render: (_v, r) => {
            const conflictSeats = (r.seats || []).filter((s) => s.canReassign);
            if (conflictSeats.length === 0 || !r.matchedTrip || !r.resolvedTripRoute) {
                return <Text type="secondary">—</Text>;
            }
            return (
                <Space size={[4, 4]} wrap>
                    {conflictSeats.map((s) => (
                        <Button
                            key={`reassign-${r.key}-${s.seatNumber}`}
                            size="small"
                            onClick={() => onOpenReassign(
                                r.key,
                                r.pnr,
                                r.matchedTrip.tripId,
                                r.resolvedTripRoute.tripRouteId,
                                s.seatNumber,
                            )}
                        >
                            {`Pick another seat for ${s.seatNumber}`}
                        </Button>
                    ))}
                </Space>
            );
        },
    },
];

// Map the system's ticket status to a tag colour. Blocked is the typical
// status that imported tickets are sitting in when they're cancelled in
// the source file — we want it to stand out.
const renderTicketStatusTag = (status) => {
    const normalised = (status || '').toString().toLowerCase();
    if (normalised === 'blocked') return <Tag color="orange">Blocked</Tag>;
    if (normalised === 'confirmed') return <Tag color="green">Confirmed</Tag>;
    if (normalised === 'cancelled') return <Tag>Cancelled</Tag>;
    return <Tag>{status || '—'}</Tag>;
};

const cancellationColumns = [
    { title: 'Original PNR', dataIndex: 'pnr', key: 'pnr', width: 120 },
    { title: 'Journey Date', dataIndex: 'journeyDate', key: 'journeyDate', width: 110 },
    { title: 'Service', dataIndex: 'serviceNumber', key: 'serviceNumber', width: 170 },
    {
        title: 'Existing Ticket',
        key: 'existingTicket',
        render: (_v, r) => (
            r.existingTicket
                ? (
                    <Space size={[4, 4]} wrap>
                        <Text code>#{r.existingTicket.ticketId}</Text>
                        {renderTicketStatusTag(r.existingTicket.ticketStatus)}
                        <Text type="secondary">
                            seats: {(r.existingTicket.seatNumbers || []).join(', ') || '—'}
                        </Text>
                    </Space>
                )
                : <Text type="secondary">No match</Text>
        ),
    },
    {
        title: 'Action',
        key: 'verdict',
        width: 160,
        render: (_v, r) => (
            r.canRelease
                ? <Tag color="orange">Will release seats</Tag>
                : <Tag color="default">No action</Tag>
        ),
    },
    { title: 'Reason', dataIndex: 'reason', key: 'reason' },
];

export const renderValidationResults = (
    validation,
    tickets,
    selectedValidKeys,
    setSelectedValidKeys,
    selectedCancellationKeys,
    setSelectedCancellationKeys,
    ignoredTickets,
    ineligibleTickets,
    seatOverrides,
    onOpenReassign,
    onClearOverride,
) => {
    // Pre-validation state: the page-level action bar already prompts the
    // operator to validate, so we just surface any file-level problems here.
    // If there's nothing to flag, render nothing.
    if (!validation) {
        if (!isValidArrayWithValues(ineligibleTickets) && !isValidArrayWithValues(ignoredTickets)) {
            return null;
        }
        return (
            <Space direction="vertical" style={{ width: '100%' }} size={12}>
                {isValidArrayWithValues(ineligibleTickets) && (
                    <Alert
                        type="error"
                        showIcon
                        message={`${ineligibleTickets.length} ticket(s) are not eligible to be booked — mandatory field(s) missing in the file.`}
                    />
                )}
                {isValidArrayWithValues(ignoredTickets) && (
                    <Alert
                        type="warning"
                        showIcon
                        message={`${ignoredTickets.length} ticket(s) will be skipped — status is not Booked/Blocked/Cancel.`}
                    />
                )}
            </Space>
        );
    }

    const { valid, invalid } = buildValidationBreakdown(validation, tickets, seatOverrides);
    const hasAnything = isValidArrayWithValues(validation.results)
        || isValidArrayWithValues(validation.cancellations)
        || isValidArrayWithValues(ignoredTickets);
    if (!hasAnything) {
        return <Empty description="No validation results to show." />;
    }

    const hasFileIssues = isValidArrayWithValues(ineligibleTickets) || isValidArrayWithValues(ignoredTickets);

    return (
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
            {/* File-level problems fold into a single collapsible card. */}
            {hasFileIssues && (
                <Collapse>
                    <Panel
                        key="file-issues"
                        header={(
                            <Space>
                                <CloseCircleTwoTone twoToneColor="#ff4d4f" />
                                <Text strong>
                                    {`File issues (${ineligibleTickets.length + ignoredTickets.length})`}
                                </Text>
                                <Text type="secondary">
                                    {`${ineligibleTickets.length} missing mandatory data · ${ignoredTickets.length} unknown status`}
                                </Text>
                            </Space>
                        )}
                    >
                        {isValidArrayWithValues(ineligibleTickets) && (
                            <>
                                <Text strong>Not eligible to be booked — mandatory fields missing</Text>
                                <Table
                                    rowKey={(record) => `inel-${record.tripKey}__${record.pnr || record.sourceRows.join(',')}`}
                                    size="small"
                                    pagination={{ pageSize: 5 }}
                                    style={{ marginTop: 8 }}
                                    dataSource={ineligibleTickets}
                                    columns={[
                                        { title: 'PNR', dataIndex: 'pnr', key: 'pnr', width: 110 },
                                        { title: 'Journey Date', dataIndex: 'journeyDate', key: 'journeyDate', width: 110 },
                                        { title: 'Route', dataIndex: 'route', key: 'route' },
                                        {
                                            title: 'Missing',
                                            key: 'missing',
                                            render: (_v, r) => (
                                                <Space size={[4, 4]} wrap>
                                                    {(r.missingMandatoryFields || []).map((label) => (
                                                        <Tag color="red" key={label}>{label}</Tag>
                                                    ))}
                                                </Space>
                                            ),
                                        },
                                    ]}
                                />
                            </>
                        )}
                        {isValidArrayWithValues(ignoredTickets) && (
                            <div style={{ marginTop: 12 }}>
                                <Text strong>Skipped — status not Booked/Blocked/Cancel</Text>
                                <Table
                                    rowKey={(record) => `ignored-${record.tripKey}__${record.pnr}`}
                                    size="small"
                                    pagination={{ pageSize: 5 }}
                                    style={{ marginTop: 8 }}
                                    dataSource={ignoredTickets}
                                    columns={[
                                        { title: 'PNR', dataIndex: 'pnr', key: 'pnr', width: 110 },
                                        { title: 'Journey Date', dataIndex: 'journeyDate', key: 'journeyDate', width: 110 },
                                        { title: 'Status (file)', dataIndex: 'status', key: 'status', width: 130 },
                                        {
                                            title: 'Seats',
                                            key: 'seats',
                                            render: (_v, r) => (r.seats || []).map((s) => s.seatNumber).join(', '),
                                        },
                                    ]}
                                />
                            </div>
                        )}
                    </Panel>
                </Collapse>
            )}

            {/* Hide the Valid / Invalid cards entirely when both are zero —
                typically when the file only contains Cancel rows. In that
                case the operator's focus should be on the Tickets-to-release
                card below. */}
            {(valid.length > 0 || invalid.length > 0) && (
                <Card
                    size="small"
                    title={(
                        <Space>
                            <CheckCircleTwoTone twoToneColor="#52c41a" />
                            <Text strong>{`Valid tickets — can be imported (${valid.length})`}</Text>
                            {valid.length > 0 && (
                                <Text type="secondary">
                                    {`· ${selectedValidKeys.length} of ${valid.length} selected`}
                                </Text>
                            )}
                        </Space>
                    )}
                >
                    {valid.length === 0
                        ? <Empty description="No tickets in the file are currently importable." />
                        : (
                            <Table
                                rowKey="key"
                                columns={validTicketColumns}
                                dataSource={valid}
                                pagination={{ pageSize: 10, showSizeChanger: true }}
                                scroll={{ x: 'max-content' }}
                                size="small"
                                rowSelection={{
                                    selectedRowKeys: selectedValidKeys,
                                    onChange: (keys) => setSelectedValidKeys(keys),
                                    selections: [
                                        Table.SELECTION_ALL,
                                        Table.SELECTION_NONE,
                                        Table.SELECTION_INVERT,
                                    ],
                                }}
                            />
                        )
                    }
                </Card>
            )}

            {(valid.length > 0 || invalid.length > 0) && (
                <Card
                    size="small"
                    title={(
                        <Space>
                            <CloseCircleTwoTone twoToneColor="#ff4d4f" />
                            <Text strong>{`Invalid tickets — cannot be imported (${invalid.length})`}</Text>
                        </Space>
                    )}
                >
                    {invalid.length === 0
                        ? <Empty description="No invalid tickets — every row in the file passed validation." />
                        : (
                            <Table
                                rowKey="key"
                                columns={buildInvalidTicketColumns(onOpenReassign)}
                                dataSource={invalid}
                                pagination={{ pageSize: 10, showSizeChanger: true }}
                                scroll={{ x: 'max-content' }}
                                size="small"
                            />
                        )
                    }
                </Card>
            )}

            {(() => {
                const allCancellations = isValidArray(validation.cancellations) ? validation.cancellations : [];
                const releasable = allCancellations.filter((c) => c.canRelease);
                const noMatch = allCancellations.filter((c) => !c.canRelease);
                if (allCancellations.length === 0) {
                    return null;
                }
                // Group existing tickets by their current status so the
                // operator can see at a glance "5 Blocked tickets ready to
                // release". Most of the time these are Blocked (from a prior
                // bulk import).
                const statusCounts = {};
                releasable.forEach((row) => {
                    const status = row.existingTicket && row.existingTicket.ticketStatus
                        ? row.existingTicket.ticketStatus
                        : 'Unknown';
                    statusCounts[status] = (statusCounts[status] || 0) + 1;
                });
                const statusBreakdown = Object.keys(statusCounts).map((status) => (
                    `${statusCounts[status]} ${status}`
                )).join(', ');
                return (
                    <>
                        {releasable.length > 0 && (
                            <Card
                                size="small"
                                title={(
                                    <Space wrap>
                                        <Tag color="orange">↩</Tag>
                                        <Text strong>
                                            {`Tickets to release (${releasable.length})`}
                                        </Text>
                                        <Text type="secondary">
                                            {statusBreakdown}
                                        </Text>
                                        <Text type="secondary">
                                            {`· ${selectedCancellationKeys.length} of ${releasable.length} selected`}
                                        </Text>
                                    </Space>
                                )}
                            >
                                <Alert
                                    type="warning"
                                    showIcon
                                    style={{ marginBottom: 12 }}
                                    message={(
                                        <span>
                                            These rows are marked <Text strong>Cancel</Text> in the
                                            upload and a matching ticket already exists in the system
                                            {statusBreakdown ? ` (${statusBreakdown})` : ''}.
                                            Selecting a row will <Text strong>release the seats</Text>
                                            {' '}held by that ticket — the same behaviour as the
                                            travels server's release flow. The ticket record itself
                                            stays intact for audit; only the seats become available.
                                        </span>
                                    )}
                                />
                                <Table
                                    rowKey={(record) => `${record.tripKey}__${record.pnr}`}
                                    columns={cancellationColumns}
                                    dataSource={releasable}
                                    pagination={{ pageSize: 10, showSizeChanger: true }}
                                    scroll={{ x: 'max-content' }}
                                    size="small"
                                    rowSelection={{
                                        selectedRowKeys: selectedCancellationKeys,
                                        onChange: (keys) => setSelectedCancellationKeys(keys),
                                        selections: [
                                            Table.SELECTION_ALL,
                                            Table.SELECTION_NONE,
                                            Table.SELECTION_INVERT,
                                        ],
                                    }}
                                />
                            </Card>
                        )}
                        {noMatch.length > 0 && (
                            <Card
                                size="small"
                                title={(
                                    <Space>
                                        <Tag>—</Tag>
                                        <Text strong>
                                            {`Cancellations with no match in the system (${noMatch.length})`}
                                        </Text>
                                    </Space>
                                )}
                            >
                                <Paragraph type="secondary" style={{ marginBottom: 8 }}>
                                    These rows are marked Cancel in the upload but no previously-imported
                                    ticket was found for their original PNR. No action is needed — they
                                    are listed here only for transparency.
                                </Paragraph>
                                <Table
                                    rowKey={(record) => `nomatch-${record.tripKey}__${record.pnr}`}
                                    columns={cancellationColumns}
                                    dataSource={noMatch}
                                    pagination={{ pageSize: 5, showSizeChanger: true }}
                                    scroll={{ x: 'max-content' }}
                                    size="small"
                                />
                            </Card>
                        )}
                    </>
                );
            })()}

            <Collapse>
                <Panel header={<Text>Per-trip seat-level diagnostics</Text>} key="by-trip">
                    <Collapse>
                        {validation.results.map((groupResult) => {
                            const importableCount = groupResult.tickets.filter((t) => t.canImport).length;
                            const blocked = groupResult.tickets.length - importableCount;
                            const headerColor = groupResult.tripFound
                                ? (blocked === 0 ? 'green' : 'orange')
                                : 'red';
                            const header = (
                                <Space>
                                    {groupResult.tripFound
                                        ? <CheckCircleTwoTone twoToneColor="#52c41a" />
                                        : <CloseCircleTwoTone twoToneColor="#ff4d4f" />}
                                    <Text strong>{groupResult.journeyDate}</Text>
                                    <Text>{groupResult.serviceNumber || groupResult.route}</Text>
                                    <Tag color={headerColor}>
                                        {`${importableCount} importable / ${groupResult.tickets.length} ticket(s)`}
                                    </Tag>
                                </Space>
                            );
                            return (
                                <Panel header={header} key={groupResult.tripKey}>
                                    {!groupResult.tripFound && (
                                        <Alert
                                            style={{ marginBottom: 12 }}
                                            type="error"
                                            showIcon
                                            message={groupResult.tripIssue || 'Trip could not be matched.'}
                                        />
                                    )}
                                    {groupResult.tripFound && groupResult.trip && (
                                        <Card size="small" style={{ marginBottom: 12 }}>
                                            <Row gutter={[16, 8]}>
                                                <Col span={6}><Text type="secondary">Trip ID:</Text> {groupResult.trip.tripId}</Col>
                                                <Col span={6}><Text type="secondary">Schedule:</Text> {groupResult.trip.scheduleDescription || groupResult.trip.scheduleId}</Col>
                                                <Col span={6}><Text type="secondary">Origin → Destination:</Text> {groupResult.trip.origin} → {groupResult.trip.destination}</Col>
                                                <Col span={6}><Text type="secondary">Vacant seats:</Text> {groupResult.trip.availableSeats}</Col>
                                            </Row>
                                        </Card>
                                    )}
                                    <Table
                                        rowKey="pnr"
                                        pagination={false}
                                        size="small"
                                        dataSource={groupResult.tickets}
                                        columns={[
                                            { title: 'PNR', dataIndex: 'pnr', key: 'pnr', width: 110 },
                                            {
                                                title: 'Verdict',
                                                key: 'verdict',
                                                width: 130,
                                                render: (_value, record) => (
                                                    record.canImport
                                                        ? <Tag color="green" icon={<CheckCircleTwoTone twoToneColor="#52c41a" />}>Can import</Tag>
                                                        : <Tag color="red" icon={<CloseCircleTwoTone twoToneColor="#ff4d4f" />}>Cannot import</Tag>
                                                ),
                                            },
                                            {
                                                title: 'Seats',
                                                key: 'seats',
                                                render: (_v, r) => renderSeatTags(r.seats),
                                            },
                                            {
                                                title: 'Reason',
                                                key: 'reason',
                                                render: (_value, record) => (
                                                    isValidArrayWithValues(record.issues)
                                                        ? (
                                                            <ul style={{ margin: 0, paddingLeft: 18 }}>
                                                                {record.issues.map((issue, idx) => (
                                                                    <li key={`${record.pnr}-issue-${idx}`}>{issue}</li>
                                                                ))}
                                                            </ul>
                                                        )
                                                        : <Text type="secondary">No issues</Text>
                                                ),
                                            },
                                        ]}
                                    />
                                </Panel>
                            );
                        })}
                    </Collapse>
                </Panel>
            </Collapse>
        </Space>
    );
};

// Silence unused-import warnings.
void isValid;
